import { useTranslations } from "@fenetech/translations";
import { AlertColor, Button, Grid } from "@mui/material";
import LabeledText from "components/Common/LabeledText";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ESignatureAPI from "./ESignatureAPI";
import { isNullOrWhiteSpace } from "helpers/objects";
import CheckBoxForm from "components/Common/CheckBoxForm";
import { ESignatureRedirectSourceEnum } from "helpers/enums";

interface IProps {
    isEditMode: boolean,
    mfgCustomerID: string,
    mfgSiteID: string,
    setIsLoading: (value: React.SetStateAction<boolean>) => void,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>,
    authCustomerGUID: string | null
}

const ESignatureConnection: React.FC<IProps> = ({ isEditMode, mfgCustomerID, mfgSiteID, setIsLoading, setAlert, authCustomerGUID }: IProps) => {

    const tm = useTranslations();

    const [isPingTestSuccessful, setIsPingTestSuccessful] = useState<boolean | null>(null);
    const [pingTestMfgSite, setPingTestMfgSite] = useState<string | null>(null);

    useEffect(() => {
        //reset the ping test once a new mfgSite is selected
        if (isPingTestSuccessful !== null && mfgSiteID !== pingTestMfgSite) {
            setIsPingTestSuccessful(null);
            setPingTestMfgSite(null);
        }
    }, [mfgSiteID, isPingTestSuccessful, pingTestMfgSite]);

    const callbackURI = useMemo(() => {
        return document.getElementsByTagName("base")[0].href + "setup/esign/callback";
    }, []);

    const pingTest = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await ESignatureAPI.PingTest(mfgCustomerID, mfgSiteID);

            if (res.loginRequested && authCustomerGUID === null) {
                const authURIResponse = await ESignatureAPI.GetAuthURIFromCustomer(mfgCustomerID, mfgSiteID, callbackURI, ESignatureRedirectSourceEnum.Setup);
                if (!isNullOrWhiteSpace(authURIResponse.message)) {
                    setAlert([tm.Get(authURIResponse.message), "error"]);
                    setIsPingTestSuccessful(false);
                } else {
                    window.location.assign(authURIResponse.payload);
                }
            } else if (!isNullOrWhiteSpace(res.message)) {
                setAlert([tm.Get(res.message), "error"]);
                setIsPingTestSuccessful(false);
            } else {
                setIsPingTestSuccessful(res.payload);
            }
            
            setPingTestMfgSite(mfgSiteID);
        }
        finally {
            setIsLoading(false);
        }
    }, [mfgCustomerID, mfgSiteID, setIsLoading, setAlert, callbackURI, authCustomerGUID, tm]);

    return <Grid container spacing={1} mb={1}>

        <Grid item xs={12}>
            <CheckBoxForm
                readOnly={false}
                fieldName="isEnabled"
                label={tm.Get("E-Signature Enabled")}
                disabled={!isEditMode}
            />
        </Grid>

        <Grid item xs={12}>
            <CheckBoxForm
                readOnly={false}
                fieldName="isProduction"
                label={tm.Get("Use Production Environment")}
                disabled={!isEditMode}
            />
        </Grid>

        <Grid item container xs={12}>
            <Grid item xs={12} sm={2}>
                <Button variant="contained" disabled={isEditMode}  onClick={pingTest}>
                    {tm.Get("API Status")}
                </Button>
            </Grid>
            <Grid item xs={12} sm alignContent="center">
                {isPingTestSuccessful !== null &&
                    <LabeledText
                        label=""
                        text={isPingTestSuccessful ? tm.Get("Success") : tm.Get("Failed")}
                        isChecked={isPingTestSuccessful}
                    />
                }
            </Grid>
        </Grid>
    </Grid>;
}

export default ESignatureConnection;